import { ExitSVG } from '@sbercloud/uikit-product-icons';
import { CSS_BREAKPOINTS } from '@sbercloud/uikit-product-utils';
import { captureMessage } from '@sentry/nextjs';
import { ButtonFunction } from '@snack-uikit/button';
import { Dropdown } from '@snack-uikit/dropdown';
import Cookies from 'js-cookie';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { useAuth } from 'react-oidc-context';

import { LoadingOverlay } from '@/components/static';
import { OIDC_COOKIES } from '@/constant/oidc';
import { usePageDataCtx, useServerDataCtx } from '@/hooks/ctx';
import { getGlobalArgs } from '@/root/global';
import { useLayoutType } from '@/root/src/hooks';
import { isLargeThan } from '@/utils/layout';

import { DropDownContent } from './DropdownContent';
import s from './styles.module.scss';
import { FullUserInitials, ProfileBadge } from './utils';

export function UserDetails() {
  const { isAuthenticated, signoutRedirect, user, clearStaleState, removeUser, revokeTokens } = useAuth();
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const [isLoadingLogout, setIsLoadingLogout] = useState(false);
  const pathname = usePathname();
  const { authRequired } = usePageDataCtx();

  const { headers } = useServerDataCtx();
  const layoutType = useLayoutType(CSS_BREAKPOINTS, headers.xLayoutType);

  if (!isAuthenticated) return null;

  const handleLogout = async () => {
    setIsLoadingLogout(true);
    try {
      setIsOpenDropdown(false);
      if (!authRequired) {
        Cookies.set(OIDC_COOKIES.redirectBackUrl, pathname);
      }
      await signoutRedirect({
        id_token_hint: user?.id_token,
        post_logout_redirect_uri: getGlobalArgs('NEXT_PUBLIC_OIDC_REDIRECT_URI'),
      });

      await removeUser();
      await revokeTokens();
      return clearStaleState();
    } catch (err) {
      console.log(`failed to logout, ${err}`);
      captureMessage(`failed to logout user, ${err}`, { level: 'error' });
      return null;
    } finally {
      setIsLoadingLogout(false);
    }
  };

  if (isLargeThan('tablet', layoutType)) {
    return (
      <Dropdown
        placement='bottom-end'
        offset={20}
        open={isOpenDropdown}
        onOpenChange={setIsOpenDropdown}
        content={<DropDownContent isLoadingLogout={isLoadingLogout} handleLogout={handleLogout} />}
      >
        <ProfileBadge />
      </Dropdown>
    );
  }

  return (
    <LoadingOverlay isLoading={isLoadingLogout}>
      <div className={s.auth_dropdown_content}>
        <ProfileBadge />
        <FullUserInitials />
        <ButtonFunction iconPosition='before' icon={<ExitSVG />} onClick={handleLogout} />
      </div>
    </LoadingOverlay>
  );
}
